import type { UniversalWorkDetail } from "@excellent/core/verification-universal-surfaces";
import { ApiError } from "@excellent/ui/api";

export const CANONICAL_WORK_ID_PATTERN =
  /^work:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type WorkDetailRequest =
  | { kind: "ready"; params: { id: string } }
  | { kind: "invalid"; title: "Invalid Work id"; message: string };

export type WorkDetailErrorState = {
  kind: "not-found" | "unavailable";
  title: string;
  message: string;
};

type Receipt = UniversalWorkDetail["incomplete"]["receipts"][number];

export function resolveWorkDetailRequest(id: string): WorkDetailRequest {
  if (!CANONICAL_WORK_ID_PATTERN.test(id)) {
    return {
      kind: "invalid",
      title: "Invalid Work id",
      message: "Work links require a canonical work:<uuid> identifier.",
    };
  }
  return { kind: "ready", params: { id } };
}

function errorStatus(error: unknown): number | null {
  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof error.status === "number"
  ) {
    return error.status;
  }
  return null;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.error;
  if (error instanceof Error) return error.message;
  if (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof error.error === "string"
  ) {
    return error.error;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  if (typeof error === "string") return error;
  return "The Work detail request did not complete.";
}

export function workDetailErrorState(error: unknown): WorkDetailErrorState {
  if (errorStatus(error) === 404) {
    return {
      kind: "not-found",
      title: "Work not found",
      message: "No Work is available for this identifier in the current tenant.",
    };
  }
  return {
    kind: "unavailable",
    title: "Work detail unavailable",
    message: errorMessage(error),
  };
}

export function canInspectReceiptEvidence(
  detail: UniversalWorkDetail,
  receipt: Receipt,
  bucket: "current" | "history",
): boolean {
  if (bucket !== "current") return false;

  const authority = detail.actionAuthority;
  const binding = receipt.payload.binding;
  return (
    authority.completionAuthority === "CURRENT_PROSPECTIVE" &&
    authority.currentReceiptIds.includes(receipt.payload.id) &&
    authority.currentAttemptIds.includes(binding.attemptId) &&
    authority.currentContractBindings.some(
      (candidate) =>
        candidate.contractId === binding.contractId &&
        candidate.contractVersion === binding.contractVersion &&
        candidate.contractDigest === binding.contractDigest,
    )
  );
}

export function historyRecordCount(detail: UniversalWorkDetail): number {
  return Object.values(detail.incomplete).reduce((total, records) => total + records.length, 0);
}
